// Controller reposnsible for logic related to the list of search map cards as
// a whole, mainly incrementally updating the list when map is panned/zoomed.
// It does this by: showing or hiding existing cards and loading missing cards
// from the server and merging them with existing cards in correct order.
const MAX_CACHED_CARDS = 50

stimulusApp.register(
  "search-map-list",
  class extends Stimulus.Controller {
    static outlets = ["search-map-card"]
    static targets = ["notFound", "loader"]
    static values = {
      ids: Array,
      uri: String,
      maxCachedCards: { type: Number, default: MAX_CACHED_CARDS },
    }

    #hideSequence = 0

    connect() {
      if (Detector.isMobile) {
        document.addEventListener(
          "search-map:clear-selection",
          this.#unHighlightCard,
        )
      }
    }

    disconnect() {
      if (Detector.isMobile) {
        document.removeEventListener(
          "search-map:clear-selection",
          this.#unHighlightCard,
        )
      }
    }

    findCardById(id) {
      return this.searchMapCardOutlets.find((card) => card.idValue === id)
    }

    idsValueChanged(ids) {
      let existingIds = this.updateExistingCards(ids)
      let newIds = ids.filter((id) => !existingIds.includes(id))

      this.pruneHiddenCards()

      if (newIds.length > 0) {
        this.fetchNewCards(newIds, this.uriValue)
      }

      if (this.hasNotFoundTarget) {
        let hasSomethingWrong = Boolean(
          this.element.querySelector(".smth-wrong"),
        )
        this.notFoundTarget.classList.toggle(
          "hidden",
          Boolean(ids.length) || hasSomethingWrong,
        )
      }
    }

    updateExistingCards = (ids) => {
      let existingIds = []

      this.withStableCardSelection(() => {
        this.searchMapCardOutlets.forEach((card) => {
          if (ids.includes(card.idValue)) {
            existingIds.push(card.idValue)
            card.show()
          } else {
            card.hide(++this.#hideSequence)
          }
        })
      })

      console.log("updating existing cards")
      document.dispatchEvent(new Event("map:update-existing-cards"))

      return existingIds
    }

    fetchNewCards = async (newIds, baseUri, { retry = true } = {}) => {
      this.setLoaderVisibility(true)
      const response = await RailsRequest.get(
        HB.addArrayToParams(baseUri, newIds, "place_ids"),
        { query: { incremental: true } },
      )

      if (response.statusCode === 403 && retry) {
        await HB.proofOfWork.solve((await response.json).proof_of_work)
        return this.fetchNewCards(newIds, baseUri, { retry: false })
      }

      this.setLoaderVisibility(false)

      if (response.statusCode === 403) {
        console.error("Proof of work did not unlock the map cards")
        return
      }

      const html = await response.html
      const container = document.createElement("div")
      container.innerHTML = html
      this.mergeNewWithCurrentCards(
        container.querySelector(`[data-controller~="${this.identifier}"]`),
      )
      this.pruneHiddenCards()

      document.dispatchEvent(new Event("map:fetch-new-cards"))
    }

    setLoaderVisibility = (visible) => {
      this.loaderTargets.forEach((loader) => (loader.hidden = !visible))
    }

    mergeNewWithCurrentCards = (newCards) => {
      const currentCards = this.searchMapCardOutlets.map((card) => card.element)
      const currentIds = currentCards.map((card) => card.id)
      let index = 0

      this.withStableCardSelection(() => {
        while (newCards.firstChild) {
          const newCard = newCards.firstChild
          newCards.removeChild(newCard)
          const newCardSortBy = JSON.parse(newCard.dataset.sortBy)

          if (!currentIds.includes(newCard.id)) {
            while (
              index < currentCards.length &&
              this.cardIsBefore(currentCards[index], newCardSortBy)
            ) {
              index++
            }

            if (index < currentCards.length) {
              this.element.insertBefore(newCard, currentCards[index])
            } else {
              this.element.appendChild(newCard)
            }
          }
        }
      })
    }

    cardIsBefore = (card, sortByValue) =>
      HB.arrayLessThan(JSON.parse(card.dataset.sortBy), sortByValue)

    pruneHiddenCards = () => {
      const cards = this.searchMapCardOutlets.map((card) => card.element)
      if (cards.length <= this.maxCachedCardsValue) return

      const hiddenCards = cards.filter((card) => card.hidden)
      const visibleCount = cards.length - hiddenCards.length
      const maxHiddenCards = Math.max(
        0,
        this.maxCachedCardsValue - visibleCount,
      )

      if (hiddenCards.length <= maxHiddenCards) return

      hiddenCards
        .sort((a, b) => a.dataset.hiddenSequence - b.dataset.hiddenSequence)
        .slice(0, hiddenCards.length - maxHiddenCards)
        .forEach((card) => card.remove())
    }

    withStableCardSelection = (callback) => {
      let selectedId = HB.mapHelpers.selectedMarkerId()
      let selectedCard = this.findCardById(selectedId)
      if (selectedCard) {
        // As long as a card is marked as focusing, autofocus controller will not do anything.
        selectedCard.focusing = true
      }

      callback.call()

      if (selectedCard) {
        this.element.scrollLeft = selectedCard.element.offsetLeft - 10
        selectedCard.focusing = false
      }
    }

    #unHighlightCard = () => {
      this.searchMapCardOutlets
        .find((card) => card.isHighlighted())
        ?.unHighlight()
    }
  },
)
